/**
 * V4 — "Construct"
 * Exposed structure. Dot-grid background. Monospace labels [01.FEATURE].
 * 3D perspective cards on hover. Elements that assemble on scroll.
 * Fonts: Space Mono (labels) + Plus Jakarta Sans (body)
 */
import { useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const MONO = "'Space Mono', monospace";
const SANS = "'Plus Jakarta Sans', sans-serif";
const GREEN = '#2EC4B6';
const ORANGE = '#ef5228';

export function LandingV4() {
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Hero: text assembles from different positions
      gsap.fromTo('.v4c-line1', { x: -100, skewX: -10, opacity: 0 }, { x: 0, skewX: 0, opacity: 1, duration: 0.8, ease: 'power4.out' });
      gsap.fromTo('.v4c-line2', { x: 100, skewX: 10, opacity: 0 }, { x: 0, skewX: 0, opacity: 1, duration: 0.8, ease: 'power4.out', delay: 0.15 });
      gsap.fromTo('.v4c-line3', { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, delay: 0.3 });
      gsap.fromTo('.v4c-meta', { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, delay: 0.5 });

      // Cursor blink
      gsap.to('.v4c-cursor', { opacity: 0, duration: 0.5, repeat: -1, yoyo: true });

      // Feature cards slide in from alternating sides
      document.querySelectorAll('.v4c-card').forEach((el, i) => {
        gsap.fromTo(el, {
          x: i % 2 === 0 ? -80 : 80,
          rotateY: i % 2 === 0 ? 15 : -15,
          opacity: 0,
        }, {
          x: 0, rotateY: 0, opacity: 1, duration: 0.7, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 80%' },
        });
      });

      // Screenshot assembly
      gsap.fromTo('.v4c-shot-1', { x: -30, rotation: -3, opacity: 0 }, {
        x: 0, rotation: -1.5, opacity: 1, duration: 0.7,
        scrollTrigger: { trigger: '.v4c-shots', start: 'top 75%' },
      });
      gsap.fromTo('.v4c-shot-2', { x: 30, rotation: 3, opacity: 0 }, {
        x: 0, rotation: 1.5, opacity: 1, duration: 0.7, delay: 0.1,
        scrollTrigger: { trigger: '.v4c-shots', start: 'top 75%' },
      });

      // Reveals
      document.querySelectorAll('.v4c-reveal').forEach((el) => {
        gsap.fromTo(el, { y: 30, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.6, scrollTrigger: { trigger: el, start: 'top 82%' },
        });
      });
    });
    return () => ctx.revert();
  }, []);

  const features = [
    { tag: '01.LIBRARY', title: 'Asset Library', desc: 'Every logo, headshot, background — organized, tagged, searchable. The single source of truth for your brand elements.', color: GREEN },
    { tag: '02.PROFILES', title: 'Entity Profiles', desc: 'Artists and venues get dedicated profiles. Assets are auto-linked. AI knows who owns what.', color: GREEN },
    { tag: '03.GENERATE', title: 'AI Generation', desc: 'Context-aware flyers. Your real logos, headshots, brand colors. Not random images from the internet.', color: ORANGE },
    { tag: '04.OUTPUT', title: 'Instant Export', desc: 'Select event → generate → print-ready artwork. Seconds, not hours. One click.', color: ORANGE },
  ];

  return (
    <div style={{ fontFamily: SANS, background: '#0a0a0a', color: '#aaa', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Dot grid background */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.07]" style={{
        backgroundImage: 'radial-gradient(circle, #fff 0.5px, transparent 0.5px)',
        backgroundSize: '24px 24px',
      }} />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(10,10,10,0.85)', backdropFilter: 'blur(8px)', borderBottom: '1px dashed rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1200px] mx-auto px-8 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-3">
            <img src="/eventtools-logo.svg" alt="" className="w-7 h-7" />
            <span style={{ fontFamily: MONO, fontSize: 13, color: '#fff', letterSpacing: '0.05em' }}>event.tools</span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <span style={{ fontFamily: MONO, fontSize: 11, color: '#555' }}>[NAV]</span>
            <a href="#features" className="text-[12px]" style={{ color: '#666' }}>features</a>
            <a href="#workflow" className="text-[12px]" style={{ color: '#666' }}>workflow</a>
          </div>
          <Link to="/signin" className="px-4 py-2 text-[12px] font-bold" style={{ fontFamily: MONO, border: `1px solid ${GREEN}`, color: GREEN }}>
            GET_ACCESS
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="min-h-screen flex items-center px-8 pt-20">
        <div className="max-w-[1200px] mx-auto w-full">
          <div className="mb-3" style={{ fontFamily: MONO, fontSize: 11, color: '#444', letterSpacing: '0.1em' }}>
            <span style={{ color: GREEN }}>[HERO]</span> AI-powered flyer builder
          </div>
          <div style={{ fontFamily: SANS }}>
            <div className="v4c-line1" style={{ fontSize: 'clamp(48px, 9vw, 120px)', fontWeight: 800, lineHeight: 0.92, color: '#fff', letterSpacing: '-0.05em' }}>
              YOUR ASSETS<span className="v4c-cursor" style={{ color: GREEN }}>_</span>
            </div>
            <div className="v4c-line2 md:ml-[15vw]" style={{ fontSize: 'clamp(48px, 9vw, 120px)', fontWeight: 800, lineHeight: 0.92, letterSpacing: '-0.05em', color: 'transparent', WebkitTextStroke: '1.5px #fff' }}>
              ALWAYS
            </div>
            <div className="v4c-line3" style={{ fontSize: 'clamp(48px, 9vw, 120px)', fontWeight: 800, lineHeight: 0.92, color: GREEN, letterSpacing: '-0.05em' }}>
              READY.
            </div>
          </div>
          <div className="v4c-meta mt-10 flex flex-col md:flex-row gap-8 items-start">
            <p className="max-w-md text-sm leading-relaxed" style={{ color: '#666' }}>
              The flyer builder that organizes every logo, headshot, and brand element — so AI generation always uses the right context. Built for nightlife promoters.
            </p>
            <div className="flex gap-4">
              <Link to="/signin" className="px-6 py-3 text-sm font-bold" style={{ fontFamily: MONO, background: GREEN, color: '#0a0a0a', fontSize: 12 }}>
                GET_STARTED →
              </Link>
              <a href="#features" className="px-6 py-3 text-sm" style={{ fontFamily: MONO, border: '1px dashed rgba(255,255,255,0.15)', color: '#666', fontSize: 12 }}>
                EXPLORE ↓
              </a>
            </div>
          </div>
          {/* Decorative construction marks */}
          <div className="mt-16 flex items-center gap-4">
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: '#333' }}>[ SCROLL ]</span>
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
          </div>
        </div>
      </section>

      {/* Features — 3D cards */}
      <section id="features" className="py-24 px-8" style={{ perspective: '1200px' }}>
        <div className="max-w-[1000px] mx-auto">
          <div className="mb-12">
            <span style={{ fontFamily: MONO, fontSize: 11, color: '#444' }}>[SECTION] <span style={{ color: GREEN }}>capabilities</span></span>
          </div>
          <div className="space-y-5">
            {features.map((f, i) => (
              <div key={i} className="v4c-card group relative p-7 transition-all duration-300 hover:translate-x-2"
                style={{
                  background: '#111',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderLeft: `3px solid ${f.color}`,
                  transformStyle: 'preserve-3d',
                }}>
                <div className="flex flex-col md:flex-row md:items-start gap-5">
                  <div className="flex-shrink-0">
                    <span style={{ fontFamily: MONO, fontSize: 11, color: f.color, letterSpacing: '0.05em' }}>[{f.tag}]</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: '#fff', letterSpacing: '-0.02em' }}>{f.title}</h3>
                    <p className="text-[13px] leading-relaxed" style={{ color: '#666' }}>{f.desc}</p>
                  </div>
                </div>
                {/* Hover glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                  style={{ background: `linear-gradient(90deg, ${f.color}08, transparent 50%)` }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots — assembled, slightly rotated */}
      <section className="v4c-shots py-20 px-8">
        <div className="max-w-[1100px] mx-auto">
          <div className="mb-8">
            <span style={{ fontFamily: MONO, fontSize: 11, color: '#444' }}>[SECTION] <span style={{ color: ORANGE }}>product</span></span>
          </div>
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6" style={{ perspective: '800px' }}>
            <div className="v4c-shot-1 overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)', transform: 'rotate(-1.5deg)' }}>
              <div className="px-3 py-2 flex items-center gap-2" style={{ background: '#111', borderBottom: '1px dashed rgba(255,255,255,0.06)' }}>
                <div className="w-2 h-2 rounded-full" style={{ background: ORANGE }} />
                <span style={{ fontFamily: MONO, fontSize: 10, color: '#555' }}>screenshot_events.png</span>
              </div>
              <img src="/screenshot-events.png" alt="" className="w-full" />
            </div>
            <div className="v4c-shot-2 overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)', transform: 'rotate(1.5deg)' }}>
              <div className="px-3 py-2 flex items-center gap-2" style={{ background: '#111', borderBottom: '1px dashed rgba(255,255,255,0.06)' }}>
                <div className="w-2 h-2 rounded-full" style={{ background: GREEN }} />
                <span style={{ fontFamily: MONO, fontSize: 10, color: '#555' }}>screenshot_library.png</span>
              </div>
              <img src="/screenshot-library.png" alt="" className="w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="py-24 px-8 v4c-reveal">
        <div className="max-w-[800px] mx-auto">
          <span style={{ fontFamily: MONO, fontSize: 11, color: '#444' }}>[SECTION] <span style={{ color: GREEN }}>workflow</span></span>
          <h2 className="mt-4 mb-12" style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em' }}>
            Upload → Organize → Generate
          </h2>
          <div className="space-y-8">
            {[
              { n: '01', t: 'Upload your assets', d: 'Add logos, headshots, backgrounds. Tag and organize by artist, venue, or event.' },
              { n: '02', t: 'Build entity profiles', d: 'Link assets to artists and venues. AI knows who owns what, automatically.' },
              { n: '03', t: 'Generate flyers', d: 'Pick an event, hit generate. AI pulls the right assets. Print-ready in seconds.' },
            ].map((s, i) => (
              <div key={i} className="flex gap-6 items-start p-5" style={{ borderLeft: `2px solid ${i === 2 ? ORANGE : GREEN}30`, background: '#0d0d0d' }}>
                <span style={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, color: `${i === 2 ? ORANGE : GREEN}30` }}>{s.n}</span>
                <div>
                  <h3 className="font-bold mb-1" style={{ color: '#fff' }}>{s.t}</h3>
                  <p className="text-[13px]" style={{ color: '#666' }}>{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo with construction overlay */}
      <section className="relative h-[50vh] overflow-hidden v4c-reveal">
        <img src="/images/tools_dj_spin.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.3) grayscale(0.5)' }} />
        {/* Grid overlay on photo */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        <div className="relative z-10 h-full flex items-center justify-center px-6">
          <div className="text-center">
            <span style={{ fontFamily: MONO, fontSize: 11, color: GREEN, letterSpacing: '0.1em' }}>[NIGHTLIFE.READY]</span>
            <h2 className="mt-2" style={{ fontSize: 'clamp(28px, 5vw, 56px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em' }}>
              Built for the night.
            </h2>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 px-8 text-center v4c-reveal">
        <span style={{ fontFamily: MONO, fontSize: 11, color: '#444' }}>[CTA]</span>
        <h2 className="mt-4 mb-8" style={{ fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em' }}>
          READY_<span className="v4c-cursor" style={{ color: GREEN }}>|</span>
        </h2>
        <Link to="/signin" className="px-8 py-4 text-[12px] font-bold inline-flex items-center gap-2" style={{ fontFamily: MONO, background: GREEN, color: '#0a0a0a' }}>
          GET_ACCESS <ArrowRight size={14} />
        </Link>
      </section>

      {/* Footer */}
      <footer className="py-8 px-8" style={{ borderTop: '1px dashed rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <span style={{ fontFamily: MONO, fontSize: 11, color: '#444' }}>event.tools</span>
          <span style={{ fontFamily: MONO, fontSize: 10, color: '#333' }}>© {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
